import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import useLocalRecorder from '../hooks/useLocalRecorder';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import { fn } from '../api/functions';

// Cache-busting comment - updated for deployment v2.0
export default function AIInterviewer({ storyId, answerId, transcript, originalQuestion, onComplete }) {
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingFollowups, setIsGeneratingFollowups] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [followupAnswers, setFollowupAnswers] = useState([]);
  const { startRecording, stopRecording } = useLocalRecorder();
  const [editingFollowupId, setEditingFollowupId] = useState(null);
  const [editedTranscript, setEditedTranscript] = useState('');
  const [rerecordingFollowupId, setRerecordingFollowupId] = useState(null);
  const [cleaningFollowupId, setCleaningFollowupId] = useState(null); // Track which followup is being cleaned
  const [isFinished, setIsFinished] = useState(false);
  const [isAutoSaved, setIsAutoSaved] = useState(false); // Track if current answer has been auto-saved

  // Use a ref to store active listeners so we can clean them up
  const activeListeners = useRef([]);
  const hasGeneratedQuestions = useRef(false);

  // Clean up listeners when unmounting
  useEffect(() => {
    const listeners = [...activeListeners.current];
    return () => {
      listeners.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  // Generate follow-up questions when component mounts
  useEffect(() => {
    if (!hasGeneratedQuestions.current) {
      hasGeneratedQuestions.current = true;
      generateQuestions();
    }
  }, []); // Empty dependency array - only run once on mount

  // Load follow-up answers from Firestore on mount or when storyId/answerId changes
  useEffect(() => {
    const fetchFollowupAnswers = async () => {
      if (!storyId || !answerId) return;
      try {
        const answerRef = doc(db, "stories", storyId, "answers", answerId);
        const answerSnap = await getDoc(answerRef);
        if (answerSnap.exists()) {
          let followups = answerSnap.data().followups;
          if (!Array.isArray(followups)) followups = [];
          setFollowupAnswers(followups);
        } else {
          setFollowupAnswers([]);
        }
      } catch (err) {
        console.error("Failed to load follow-up answers:", err);
        setFollowupAnswers([]);
      }
    };
    fetchFollowupAnswers();
  }, [storyId, answerId]);

  const generateQuestions = async () => {
    setIsGeneratingFollowups(true);
    try {
      
      const context = { transcript, originalQuestion };
      const result = await fn.generateFollowupQuestions(context);
      setFollowUpQuestions(result.data.questions);
    } catch (err) {
      console.error('Error generating follow-up questions:', err);
      alert(err.message || 'Failed to generate follow-up questions. Please try again.');
    } finally {
      setIsGeneratingFollowups(false);
    }
  };

  // Function to regenerate the current question
  const regenerateCurrentQuestion = async () => {
    setIsGenerating(true);
    try {
      
      // Keep the answer context and avoid repeating the current question.
      const context = { transcript, originalQuestion, previousQuestions: followUpQuestions };

      const result = await fn.generateFollowupQuestions(context);
      const newQuestions = result.data.questions;
      
      // Replace the current question with a new one, keeping the rest
      const updatedQuestions = [...followUpQuestions];
      if (newQuestions && newQuestions.length > 0) {
        // Try to find a different question from the new set
        const currentQuestion = followUpQuestions[currentQuestionIndex];
        let questionToUse = newQuestions.find(q => q !== currentQuestion) || newQuestions[0];
        
        // If all questions are the same, try a different index
        if (questionToUse === currentQuestion && newQuestions.length > 1) {
          questionToUse = newQuestions[1];
        }
        
        updatedQuestions[currentQuestionIndex] = questionToUse;
        setFollowUpQuestions(updatedQuestions);
      }
    } catch (err) {
      console.error('Error regenerating follow-up question:', err);
      alert(err.message || 'Failed to regenerate the question. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleStartRecording = () => {
    setIsRecording(true);
    startRecording();
  };

  const handleStopRecording = async () => {
    setIsRecording(false);
    setIsSaving(true);
    try {
      const blob = await stopRecording();
      setCurrentAudioUrl(URL.createObjectURL(blob));
      
      const isRerecord = !!rerecordingFollowupId;
      const followupIdToUpdate = isRerecord ? rerecordingFollowupId : `${answerId}_followup_${Date.now()}`;
      const questionToUpdate = isRerecord 
        ? followupAnswers.find(f => f.followupId === rerecordingFollowupId).question
        : followUpQuestions[currentQuestionIndex];

      const storagePath = `stories/${storyId}/answers/${followupIdToUpdate}/audio.webm`;
      
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob);
      
      const audioUrl = await getDownloadURL(storageRef);
      
      const answerPayload = {
        question: questionToUpdate,
        transcript: "Transcription in progress...",
        audioUrl,
        timestamp: new Date().toISOString(),
        status: "processing",
        followupId: followupIdToUpdate
      };

      // Use helper to save to Firestore
      await saveFollowupAnswer({ storyId, answerId, followup: answerPayload });
      
      const transcriptionResult = await fn.manualTranscribe({ audioUrl, storyId, answerId: followupIdToUpdate });
      
      if (transcriptionResult.data && transcriptionResult.data.transcript) {
        const transcribedText = transcriptionResult.data.transcript;
        const finalAnswer = { ...answerPayload, transcript: transcribedText, status: "done" };
        // Save final answer - this is the auto-save after transcription
        await saveFollowupAnswer({ storyId, answerId, followup: finalAnswer });
        setRerecordingFollowupId(null);
        
        // Mark as auto-saved and show success message
        setIsAutoSaved(true);
        console.log('Follow-up answer auto-saved successfully');
      }
      // Always reload from Firestore after save
      const answerRef = doc(db, "stories", storyId, "answers", answerId);
      const updatedAnswerDoc = await getDoc(answerRef);
      if(updatedAnswerDoc.exists()){
        let followups = updatedAnswerDoc.data().followups;
        if (!Array.isArray(followups)) followups = [];
        setFollowupAnswers(followups);
      }
    } catch (err) {
      console.error('Error details:', err);
      alert('Failed to save your answer. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEdit = (followup) => {
    setEditingFollowupId(followup.followupId);
    setEditedTranscript(followup.transcript);
  };

  const handleSaveEdit = async (followupId) => {
    // Update Firestore first using helper
    const answerToEdit = followupAnswers.find(ans => ans.followupId === followupId);
    if (!answerToEdit) return;
    const updatedFollowup = { ...answerToEdit, transcript: editedTranscript };
    await saveFollowupAnswer({ storyId, answerId, followup: updatedFollowup });
    // Reload from Firestore
    const answerRef = doc(db, "stories", storyId, "answers", answerId);
    const answerSnap = await getDoc(answerRef);
    if (answerSnap.exists()) {
      let followups = answerSnap.data().followups;
      if (!Array.isArray(followups)) followups = [];
      setFollowupAnswers(followups);
    }
    setEditingFollowupId(null);
  };

  const handleStartRerecord = (followup) => {
    if (window.confirm("Are you sure you want to re-record? The current audio and transcript will be replaced.")) {
      setRerecordingFollowupId(followup.followupId);
      setCurrentAudioUrl(null); // Reset any previous recording state
    }
  };

  // AI Clean Up Transcript
  const handleAICleanup = async (followup) => {
    console.log('AI cleanup started for followup:', followup);
    
    if (!followup.transcript || !followup.transcript.trim()) {
      alert('No transcript text found to clean up.');
      return;
    }
    
    // Set loading state for this specific followup
    setCleaningFollowupId(followup.followupId);
    
    try {
      console.log('Calling AI cleanup with text:', followup.transcript.substring(0, 100) + '...');
      
      const result = await fn.aiCleanupTranscript({ text: followup.transcript });
      console.log('AI cleanup result:', result);
      
      const cleanedTranscript = result.data.rewrittenText;
      
      if (!cleanedTranscript) {
        alert('AI cleanup did not return any cleaned text.');
        return;
      }
      
      console.log('Cleaned transcript:', cleanedTranscript.substring(0, 100) + '...');
      
      // Update Firestore using helper
      const updatedFollowup = { ...followup, transcript: cleanedTranscript };
      await saveFollowupAnswer({ storyId, answerId, followup: updatedFollowup });
      
      // Reload from Firestore
      const answerRef = doc(db, "stories", storyId, "answers", answerId);
      const answerSnap = await getDoc(answerRef);
      if (answerSnap.exists()) {
        let followups = answerSnap.data().followups;
        if (!Array.isArray(followups)) followups = [];
        setFollowupAnswers(followups);
      }
      
      console.log('AI cleanup completed successfully');
      alert('Text cleaned up successfully!');
      
    } catch (err) {
      console.error('AI cleanup error:', err);
      alert(`AI cleanup failed: ${err.message}`);
    } finally {
      setCleaningFollowupId(null);
    }
  };

  // Helper: Save or update a follow-up answer in Firestore (deduplicate by followupId)
  async function saveFollowupAnswer({ storyId, answerId, followup }) {
    const answerRef = doc(db, "stories", storyId, "answers", answerId);
    const answerSnap = await getDoc(answerRef);
    let followups = [];
    if (answerSnap.exists()) {
      followups = answerSnap.data().followups;
      if (!Array.isArray(followups)) followups = [];
    }
    // Replace or add the followup by followupId
    const idx = followups.findIndex(f => f.followupId === followup.followupId);
    if (idx !== -1) {
      followups[idx] = followup;
    } else {
      followups.push(followup);
    }
    await setDoc(answerRef, { followups }, { merge: true });
  }

  // Advance to next follow-up question or finish
  const handleNextQuestion = () => {
    setCurrentAudioUrl(null);
    setIsAutoSaved(false); // Reset auto-saved state for next question
    if (currentQuestionIndex < followUpQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setIsFinished(true);
    }
  };

  if (isGenerating) {
    return (
      <div className="ai-interview">
        <p style={{ fontSize: "1.1rem", marginBottom: "1rem", textAlign: "center" }}>
          🤖 We would like to learn more...
        </p>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (followUpQuestions.length === 0) {
    // Show loading state if questions are being generated
    if (isGeneratingFollowups) {
      return (
        <div className="ai-interview">
          <p style={{ fontSize: "1.1rem", marginBottom: "1rem", textAlign: "center" }}>
            🤖 Generating follow-up questions...
          </p>
          <div className="loading-spinner"></div>
        </div>
      );
    }

    // Show error state if generation completed but no questions available
    return (
      <div className="ai-interview">
        <p style={{ marginBottom: "1rem" }}>
          No follow-up questions available.
        </p>
        <button onClick={() => {
          hasGeneratedQuestions.current = false;
          generateQuestions();
        }}>
          Try Again
        </button>
      </div>
    );
  }

  if (isFinished) {
    return (
      <div className="ai-interview">
        <h3 style={{ marginBottom: "1rem" }}>
          🎉 Follow-up Interview Complete
        </h3>
        <p style={{ marginBottom: "1.5rem" }}>
          Thank you! The interview is complete.
        </p>
        {/* Show a summary of all follow-up answers in read-only mode */}
        {followupAnswers.map((ans, idx) => (
          <div key={ans.followupId || idx} style={{ 
            marginBottom: "1.5rem", 
            backgroundColor: "#FFFFFF", 
            padding: "1rem", 
            borderRadius: "6px",
            border: "2px solid #C87400"
          }}>
            <div className="question" style={{ marginBottom: "0.5rem" }}>
              Follow-up {idx + 1}: {typeof ans.question === 'string' ? ans.question : ans.question?.text || ans.question}
            </div>
            <audio controls src={ans.audioUrl} style={{ width: "100%", marginTop: "0.5rem" }} />
            <div style={{ marginTop: "0.5rem" }}>
              <strong>Transcript:</strong>
              <div className="transcript" style={{ marginTop: "0.25rem" }}>
                {ans.transcript}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="ai-interview">
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
      <h3 style={{ marginBottom: "1.5rem" }}>
        🤖 Follow-up Interview
      </h3>

      {/* Show all previous followups with audio and transcript */}
      {followupAnswers.map((ans, idx) => (
        <div key={ans.followupId || idx} style={{ 
          marginBottom: "1.5rem", 
          backgroundColor: "#FFFFFF", 
          padding: "1rem", 
          borderRadius: "6px",
          border: "2px solid #C87400"
        }}>
          <div className="question" style={{ marginBottom: "0.5rem" }}>
            Follow-up {idx + 1}: {typeof ans.question === 'string' ? ans.question : ans.question?.text || ans.question}
          </div>
          <audio controls src={ans.audioUrl} style={{ width: "100%", marginTop: "0.5rem" }} />
          <div style={{ marginTop: "0.5rem" }}>
            {editingFollowupId === ans.followupId ? (
              <div>
                <textarea 
                  value={editedTranscript}
                  onChange={(e) => setEditedTranscript(e.target.value)}
                  style={{ 
                    width: '98%', 
                    minHeight: '80px', 
                    margin: '1%',
                    border: '2px solid #C87400',
                    borderRadius: '4px',
                    padding: '0.5rem'
                  }}
                />
                <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.5rem" }}>
                  <button onClick={() => handleSaveEdit(ans.followupId)}>
                    Save
                  </button>
                  <button 
                    onClick={() => setEditingFollowupId(null)}
                    style={{
                      backgroundColor: "#6B7280",
                      color: "#FFFFFF"
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <strong>Transcript:</strong>
                <div className="transcript" style={{ marginTop: "0.25rem", marginBottom: "0.5rem" }}>
                  {ans.transcript}
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <button 
                    onClick={() => handleStartEdit(ans)}
                    style={{ fontSize: "0.9rem" }}
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    onClick={() => handleAICleanup(ans)}
                    disabled={cleaningFollowupId === ans.followupId}
                    style={{
                      backgroundColor: "#6B46C1",
                      color: "#FFFFFF",
                      fontSize: "0.9rem",
                      opacity: cleaningFollowupId === ans.followupId ? 0.6 : 1,
                      cursor: cleaningFollowupId === ans.followupId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {cleaningFollowupId === ans.followupId ? '🤖 Cleaning...' : '✨ AI Clean Up'}
                  </button>
                  <button 
                    onClick={() => handleStartRerecord(ans)}
                    style={{
                      backgroundColor: "#EF4444",
                      color: "#FFFFFF",
                      fontSize: "0.9rem"
                    }}
                  >
                    🔁 Re-record
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* UI for new questions or re-recording */}
      {rerecordingFollowupId ? (
        // Re-recording UI
        <div style={{ 
          marginTop: '1rem', 
          padding: '1.5rem', 
          border: '2px dashed #EF4444',
          backgroundColor: '#FEF2F2',
          borderRadius: '8px'
        }}>
          <h4 style={{ marginBottom: "1rem" }}>
            Re-recording for: "{followupAnswers.find(f => f.followupId === rerecordingFollowupId)?.question}"
          </h4>
          {/* Recording controls */}
          {!currentAudioUrl ? (
            <div>
              {isRecording ? (
                <div style={{ textAlign: "center", margin: "1rem 0" }}>
                  <div 
                    className="status" 
                    style={{ 
                      marginBottom: "0.5rem", 
                      fontSize: "1.1rem",
                      color: "#DC2626",
                      animation: "pulse 1.5s ease-in-out infinite"
                    }}
                  >
                    🔴 Recording...
                  </div>
                  <button 
                    onClick={handleStopRecording}
                    style={{
                      backgroundColor: "#061D3D",
                      color: "#F0E6D2"
                    }}
                  >
                    Stop Re-recording
                  </button>
                </div>
              ) : (
                <button 
                  onClick={handleStartRecording} 
                  disabled={isSaving}
                >
                  🎙️ Start Re-recording
                </button>
              )}
            </div>
          ) : (
             <p>Re-recording complete. Processing...</p>
          )}
          <button 
            onClick={() => setRerecordingFollowupId(null)}
            style={{
              backgroundColor: "#6B7280",
              color: "#FFFFFF",
              marginTop: "1rem"
            }}
          >
            Cancel Re-record
          </button>
        </div>
      ) : (
        <div>
          {/* Show question and recording controls only if not all questions are answered */}
          {followupAnswers.length < followUpQuestions.length && (
            <>
              <div style={{
                backgroundColor: "#F0E6D2",
                border: "2px solid #C87400",
                borderRadius: "8px",
                padding: "1.5rem",
                marginBottom: "1.5rem"
              }}>
                <div className="followup-question-container">
                  <p className="question followup-question-text">
                    {typeof followUpQuestions[currentQuestionIndex] === 'string' ? followUpQuestions[currentQuestionIndex] : followUpQuestions[currentQuestionIndex]?.text || followUpQuestions[currentQuestionIndex]}
                  </p>
                  <button 
                    onClick={regenerateCurrentQuestion}
                    disabled={isGenerating}
                    className="followup-refresh-button"
                    title="Generate a different follow-up question"
                  >
                    {isGenerating ? "🔄 Generating..." : "🔄 Refresh Question"}
                  </button>
                </div>
              </div>
              {!currentAudioUrl ? (
                <div style={{ textAlign: "center" }}>
                  {isRecording ? (
                    <div style={{ margin: "1rem 0" }}>
                      <div 
                        className="status" 
                        style={{ 
                          marginBottom: "0.5rem", 
                          fontSize: "1.1rem",
                          color: "#DC2626",
                          animation: "pulse 1.5s ease-in-out infinite"
                        }}
                      >
                        🔴 Recording...
                      </div>
                      <button 
                        onClick={handleStopRecording}
                        style={{
                          backgroundColor: "#061D3D",
                          color: "#F0E6D2"
                        }}
                      >
                        Stop Recording
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={handleStartRecording} 
                      disabled={isSaving}
                      style={{ fontSize: "1.1rem" }}
                    >
                      🎙️ Record Your Answer
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: "1rem", textAlign: "center" }}>
                  <p style={{ color: '#C87400', fontWeight: 'bold' }}>
                    🤖 Processing and auto-saving your answer...
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                    Your progress will be automatically saved once transcription is complete.
                  </p>
                </div>
              )}
            </>
          )}
          {/* Always show the Next/Finish button if a recording is done or all questions are answered */}
          {(currentAudioUrl || followupAnswers.length === followUpQuestions.length) && (
            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              {/* Auto-saved indicator */}
              {isAutoSaved && !isSaving && (
                <div style={{
                  backgroundColor: '#E6F7E6',
                  color: '#2D5C2D',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.95rem',
                  border: '1px solid #A3D3A3'
                }}>
                  ✅ Your answer has been automatically saved! You can safely continue or leave this page.
                </div>
              )}
              <button 
                onClick={handleNextQuestion} 
                disabled={isSaving}
                style={{
                  backgroundColor: isAutoSaved ? '#059669' : '#C87400',
                  transition: 'background-color 0.3s ease'
                }}
              >
                {currentQuestionIndex < followUpQuestions.length - 1 ? "Continue to Next Question" : "Finish Interview"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
